"use client";

import React from "react";
import { motion } from "framer-motion";
import { Quote, Star, Users, TrendingUp, Sparkles, Award } from "lucide-react";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchClientStats } from "@/store/slices/landingSlice";

const TestimonialsHeroSection = () => {
  const { mode } = useAppSelector((state) => state.theme);
  const isDark = mode === "dark";
  const dispatch = useAppDispatch();
  const { data: statsData } = useAppSelector(
    (state) => state.landing.clientStats,
  );

  React.useEffect(() => {
    dispatch(fetchClientStats());
  }, [dispatch]);

  const stats = [
    {
      icon: Users,
      number: statsData[0]?.value || "300+",
      label: statsData[0]?.label || "Happy Clients",
      color: "#8B31FF",
    },
    {
      icon: TrendingUp,
      number: "98%",
      label: "Satisfaction Rate",
      color: "#8B31FF",
    },
    {
      icon: Quote,
      number: statsData[2]?.value || "100+",
      label: "Testimonials",
      color: "#9333EA",
    },
  ];

  return (
    <section
      className={`relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20 transition-colors duration-700 ${
        isDark ? "bg-[#0B0F19]" : "bg-slate-50"
      }`}
    >
      {/* Top border glow — matches ServicesSection / TextTestimonialsSection */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B31FF] to-transparent opacity-70" />
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B31FF]/40 to-transparent" />

      {/* Background layer — identical to TextTestimonialsSection */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Radial mesh — dark only */}
        {isDark && (
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_55%_at_50%_0%,rgba(79,17,194,0.22),transparent)]" />
        )}

        {/* Grid pattern */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${isDark ? "opacity-[0.055]" : "opacity-[0.03]"}`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Orb 1 — top-right purple */}
        <motion.div
          className={`absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full blur-[120px] ${isDark ? "bg-[#8B31FF]/40" : "bg-[#8B31FF]/10"}`}
          animate={{ x: [0, -45, 0], y: [0, 35, 0], scale: [1, 1.12, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Orb 2 — bottom-left cyan */}
        <motion.div
          className={`absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full blur-[120px] ${isDark ? "bg-[#8B31FF]/22" : "bg-[#8B31FF]/8"}`}
          animate={{ x: [0, 55, 0], y: [0, -35, 0], scale: [1, 1.18, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Orb 3 — centre pulse */}
        <motion.div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full blur-[150px] ${isDark ? "bg-[#D946EF]/12" : "bg-[#8B31FF]/5"}`}
          animate={{ scale: [1, 1.25, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Floating particles — dark only */}
        {isDark &&
          [
            { left: "8%", top: "15%", delay: 0 },
            { left: "22%", top: "65%", delay: 0.7 },
            { left: "45%", top: "8%", delay: 1.2 },
            { left: "62%", top: "80%", delay: 0.4 },
            { left: "76%", top: "35%", delay: 1.0 },
            { left: "92%", top: "58%", delay: 1.6 },
          ].map((p, i) => (
            <motion.span
              key={i}
              className="absolute w-[3px] h-[3px] rounded-full bg-[#8B31FF]/60"
              style={{ left: p.left, top: p.top }}
              animate={{ y: [0, -18, 0], opacity: [0.25, 0.75, 0.25] }}
              transition={{
                duration: 3.5 + i * 0.4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: p.delay,
              }}
            />
          ))}
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-28">
        {/* Top badge — minimal inline label like the reference screenshot */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center mb-6"
        >
          <span
            className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] transition-colors duration-500 ${
              isDark ? "text-[#8B31FF]" : "text-[#8B31FF]"
            }`}
          >
            <Sparkles className="w-3 h-3" />
            Creative Excellence
          </span>
        </motion.div>

        {/* Main Heading — inline mixed white + cyan, single line */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12] ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          Voices of{" "}
          <span
            className={`transition-colors duration-500 ${
              isDark ? "text-[#8B31FF]" : "text-[#8B31FF]"
            }`}
          >
            Transformation
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className={`text-lg md:text-xl mb-16 max-w-2xl mx-auto leading-relaxed transition-colors duration-500 ${
            isDark ? "text-gray-400" : "text-slate-500"
          }`}
        >
          Deeply engineering digital excellence, as told by the partners who
          have journeyed with us towards market leadership.
        </motion.p>

        {/* Rating card — gradient border via box-shadow */}
        <motion.div
          initial={{ opacity: 0, scale: 0.88, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="flex justify-center mb-16"
        >
          <div
            className={`relative inline-flex flex-col items-center rounded-[1.75rem] px-14 py-9 backdrop-blur-xl transition-all duration-500 ${
              isDark
                ? "bg-white/[0.06] shadow-[0_0_50px_rgba(124,58,237,0.25),0_0_0_1px_rgba(255,255,255,0.08)]"
                : "bg-white/95 shadow-[0_8px_40px_rgba(124,58,237,0.12),0_0_0_1px_rgba(124,58,237,0.12)]"
            }`}
          >
            {/* Glowing top edge */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-[#8B31FF]/60 to-transparent" />

            {/* Award badge */}
            <div
              className={`flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border ${isDark ? "bg-[#8B31FF]/10 border-[#8B31FF]/20 text-[#8B31FF]" : "bg-[#8B31FF]/8 border-[#8B31FF]/15 text-[#8B31FF]"}`}
            >
              <Award className="w-3 h-3" />
              Top Rated Agency
            </div>

            {/* Stars */}
            <div className="flex items-center gap-1.5 mb-3">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0, rotate: -180 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  transition={{
                    delay: 0.9 + i * 0.08,
                    type: "spring",
                    stiffness: 200,
                  }}
                >
                  <Star className="w-8 h-8 fill-amber-400 text-amber-400 drop-shadow-[0_0_6px_rgba(251,191,36,0.6)]" />
                </motion.div>
              ))}
            </div>

            {/* Rating number */}
            <p
              className={`text-4xl font-black mb-1.5 transition-colors duration-500 ${isDark ? "text-white" : "text-gray-900"}`}
            >
              4.9{" "}
              <span
                className={`text-xl font-semibold ${isDark ? "text-gray-400" : "text-gray-400"}`}
              >
                /&nbsp;5.0
              </span>
            </p>

            {/* Caption */}
            <p
              className={`font-bold tracking-[0.18em] uppercase text-[11px] transition-colors duration-500 ${isDark ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
            >
              Engineered Excellence for {statsData[2]?.value || "100+"} Global
              Brands
            </p>
          </div>
        </motion.div>

        {/* Stat Cards */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 + index * 0.12 }}
              whileHover={{ y: -8, scale: 1.03 }}
              className={`group relative flex flex-col items-center rounded-lg px-6 py-8 backdrop-blur-md border overflow-hidden transition-all duration-500 ${
                isDark
                  ? "bg-white/[0.05] border-white/10 hover:border-[#8B31FF]/50 hover:shadow-[0_0_30px_rgba(124,58,237,0.2)]"
                  : "bg-white border-[#8B31FF]/12 hover:border-[#8B31FF]/35 shadow-sm hover:shadow-[0_8px_30px_rgba(124,58,237,0.1)]"
              }`}
            >
              {/* Card glow on hover */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: `radial-gradient(ellipse at center, ${stat.color}08 0%, transparent 70%)`,
                }}
              />

              {/* Icon */}
              <div
                className={`w-14 h-14 rounded-lg flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110 ${
                  isDark
                    ? "bg-white/[0.07] text-[#8B31FF] group-hover:bg-gradient-to-br group-hover:from-[#8B31FF] group-hover:to-[#8B31FF] group-hover:text-white"
                    : "bg-[#8B31FF]/8 text-[#8B31FF] group-hover:bg-gradient-to-br group-hover:from-[#8B31FF] group-hover:to-[#9333EA] group-hover:text-white"
                }`}
              >
                <stat.icon className="w-7 h-7" />
              </div>

              {/* Number */}
              <h3
                className={`text-4xl font-black mb-1.5 transition-colors duration-500 bg-clip-text ${
                  isDark
                    ? "text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-[#8B31FF] group-hover:to-[#8B31FF]"
                    : "text-gray-900 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-[#8B31FF] group-hover:to-[#9333EA]"
                }`}
              >
                {stat.number}
              </h3>

              {/* Label */}
              <p
                className={`font-bold uppercase tracking-widest text-[11px] transition-colors duration-500 ${
                  isDark
                    ? "text-gray-500 group-hover:text-[#8B31FF]"
                    : "text-gray-400 group-hover:text-[#8B31FF]"
                }`}
              >
                {stat.label}
              </p>

              {/* Bottom accent line on hover */}
              <div className="absolute bottom-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-[#8B31FF] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Bottom gradient fade — fades into TextTestimonialsSection bg */}
      <div
        className={`absolute bottom-0 left-0 right-0 h-24 pointer-events-none ${isDark ? "bg-gradient-to-t from-[#0B0F19] to-transparent" : "bg-gradient-to-t from-slate-50 to-transparent"}`}
      />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B31FF]/50 to-transparent" />
    </section>
  );
};

export default TestimonialsHeroSection;
